'use client';

import { useMemo, useState } from 'react';
import {
  Card,
  DailyView,
  DailyViewSkeleton,
  EmptyCalendarState,
  PageContainer,
} from '@/components';
import { AppearanceSettings } from '@/state/appearance-settings';
import { ClassPacks } from '../class-packs';
import { Subscriptions } from '../subscriptions';
import { GiftCards } from '../gift-cards';
import { Skeleton, TabGroup, Tab } from '@mokoboko/components';
import {
  useClassAvailability,
  useClassPackAvailability,
  useGiftCardAvailability,
  useSubscriptionAvailability,
} from '@/hooks/use-offering-availability';

// Which tabs exist is the very thing being fetched; three is the common case
// for a studio that publishes classes, packs and subscriptions. Most studios
// sell no gift cards, so their tab joins only once that answer is in.
const SKELETON_TAB_COUNT = 3;

// The settled multi-tab layout box for box: the tab bar, then the first tab,
// which is the schedule whenever the studio sells classes. A bar alone left
// the card a 48px strip that the schedule then pushed open. Below xs the tabs
// drop to text-sm, 36px instead of 40. Nothing known before the groups load
// says whether the studio sells classes at all: the tenant and appearance
// records carry no product counts.
const ExpandedViewSkeleton = ({
  classSettings,
  tabCount,
}: {
  classSettings: AppearanceSettings['class'];
  tabCount: number;
}) => (
  <PageContainer>
    <Card className="max-sm:rounded-none" contentClassName="p-5">
      <div className="flex flex-col gap-5">
        <div className="flex gap-2 rounded-2xl bg-gray-100 p-1">
          {Array.from({ length: tabCount }).map((_, i) => (
            <Skeleton
              key={i}
              isRounded
              height={40}
              width="100%"
              className="flex-1 !rounded-xl max-xs:!h-9"
            />
          ))}
        </div>

        <div className="space-y-5">
          <DailyViewSkeleton classSettings={classSettings} />
        </div>
      </div>
    </Card>
  </PageContainer>
);

interface ExpandedViewProps {
  classSettings: AppearanceSettings['class'];
  classPackSettings: AppearanceSettings['class_pack'];
  subscriptionSettings: AppearanceSettings['subscription'];
}

export const ExpandedView = ({
  classSettings,
  classPackSettings,
  subscriptionSettings,
}: ExpandedViewProps) => {
  const { hasClasses, isLoading: isClassGroupsLoading } =
    useClassAvailability();
  const { hasClassPacks, isLoading: isClassPackGroupsLoading } =
    useClassPackAvailability();
  const { hasSubscriptions, isLoading: isSubscriptionGroupsLoading } =
    useSubscriptionAvailability();
  const { hasGiftCards, isLoading: isGiftCardsLoading } =
    useGiftCardAvailability();

  const isLoading =
    isClassGroupsLoading ||
    isClassPackGroupsLoading ||
    isSubscriptionGroupsLoading ||
    isGiftCardsLoading;

  const tabs = useMemo(() => {
    const items: { label: string; value: string }[] = [];
    if (hasClasses) items.push({ label: 'Classes', value: 'classes' });
    if (hasClassPacks)
      items.push({ label: 'Class packs', value: 'class-packs' });
    if (hasSubscriptions) {
      items.push({ label: 'Memberships', value: 'subscriptions' });
    }
    if (hasGiftCards) items.push({ label: 'Gift cards', value: 'gift-cards' });
    return items;
  }, [hasClasses, hasClassPacks, hasSubscriptions, hasGiftCards]);

  const [selectedTab, setSelectedTab] = useState<string | null>(null);

  if (isLoading) {
    return (
      <ExpandedViewSkeleton
        classSettings={classSettings}
        tabCount={SKELETON_TAB_COUNT + (hasGiftCards ? 1 : 0)}
      />
    );
  }

  if (!tabs.length) {
    return (
      <PageContainer>
        <Card className="max-sm:rounded-none" contentClassName="p-5">
          <EmptyCalendarState
            className="py-6"
            title="Nothing available yet"
            description="This studio has not published any classes, packs or memberships yet."
          />
        </Card>
      </PageContainer>
    );
  }

  // Derived rather than synced: the first tab isn't known on mount, and a
  // refetch can drop the tab the user is standing on.
  const activeTab =
    selectedTab && tabs.some((tab) => tab.value === selectedTab)
      ? selectedTab
      : tabs[0].value;

  const tabContent = (
    <>
      {activeTab === 'classes' && (
        <DailyView classSettings={classSettings} groupId={null} />
      )}
      {activeTab === 'class-packs' && (
        <ClassPacks classPackSettings={classPackSettings} />
      )}
      {activeTab === 'subscriptions' && (
        <Subscriptions subscriptionSettings={subscriptionSettings} />
      )}
      {activeTab === 'gift-cards' && <GiftCards />}
    </>
  );

  if (tabs.length === 1) {
    return (
      <PageContainer>
        <Card
          className="overflow-visible max-sm:rounded-none"
          contentClassName="p-5 space-y-4"
        >
          {tabContent}
        </Card>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Card
        className="overflow-visible max-sm:rounded-none"
        contentClassName="p-5"
      >
        <div className="flex flex-col gap-5">
          <TabGroup
            label="Booking categories"
            value={activeTab}
            onValueChange={setSelectedTab}
            size="md"
            fullWidth
          >
            {/* At text-sm three labels need the whole bar a 360 screen
                gives them; any narrower and the bar scrolls sideways. */}
            {tabs.map((tab) => (
              <Tab
                key={tab.value}
                value={tab.value}
                label={tab.label}
                className="max-xs:px-2 max-xs:text-sm max-xs:leading-5 max-[359px]:px-1.5 max-[359px]:text-xs"
              />
            ))}
          </TabGroup>

          <div className="space-y-5">{tabContent}</div>
        </div>
      </Card>
    </PageContainer>
  );
};
